import { Link } from 'react-router-dom';
import type { PublicSettings, RoomTypeSummary } from '@brandonstrong/types';
import { AvailabilityBadge } from './RoomCard';
import { clockTime, money, stayDate } from '../lib/format';

/**
 * One room type is the normal case (the family holds 62 run-of-the-house
 * rooms), so it gets a full-width feature instead of a lone card: photo on
 * the left, the night, the price and a direct path to checkout on the right.
 */
export function SingleRoom({
  room,
  settings,
  query,
}: {
  room: RoomTypeSummary;
  settings: PublicSettings | undefined;
  query: string;
}) {
  const night = settings?.bookableCheckInMin;
  const out = settings?.bookableCheckOutMax;
  const amenities = room.amenities.slice(0, 8);
  // The home page renders this without a stay in the URL; the link must still
  // carry the night, or checkout has nothing to quote.
  const stayQs = query || (night && out ? `?checkIn=${night}&checkOut=${out}&adults=2` : '');
  return (
    <article className="grid gap-8 bg-night-900 p-5 sm:p-8 lg:grid-cols-[1.15fr_1fr] lg:gap-12">
      <Link
        to={`/rooms/${room.slug}${stayQs}`}
        className="relative block aspect-[4/3] overflow-hidden bg-night-800 bg-stage"
        aria-label={`Photos of ${room.name}`}
      >
        {room.primaryImage ? (
          <img
            src={room.primaryImage.url}
            alt={room.primaryImage.altText ?? room.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center font-display text-4xl font-black text-white/10">
            {room.code}
          </div>
        )}
        <span className="absolute bottom-4 right-4 bg-night-950/80 px-3 py-1.5 font-sans text-xs uppercase tracking-wider text-ink-100">
          More photos
        </span>
      </Link>

      <div className="flex flex-col">
        <p className="text-meta text-sm">Run of the house</p>
        <h2 className="mt-2 text-3xl sm:text-4xl">King or two queens</h2>
        {room.bedConfiguration && <p className="text-meta mt-2 text-sm">{room.bedConfiguration}</p>}
        {room.shortDescription && <p className="mt-4 text-ink-200">{room.shortDescription}</p>}

        <div className="mt-6 grid gap-4 border-y border-white/10 py-5 sm:grid-cols-2">
          <div>
            <p className="text-meta text-sm">Your night</p>
            <p className="mt-1 text-ink-50">
              {night ? stayDate(night) : 'Saturday'}
              {settings ? `, check in from ${clockTime(settings.checkInTime)}` : ''}
            </p>
            <p className="text-sm text-ink-400">
              {out ? `Check out ${stayDate(out)}` : 'Check out Sunday'}
              {settings ? ` by ${clockTime(settings.checkOutTime)}` : ''}
            </p>
          </div>
          <div>
            <p className="text-meta text-sm">Price</p>
            <p className="mt-1 text-ink-50">
              <span className="font-display text-3xl font-black text-yellow-500">
                {money(room.fromRate)}
              </span>{' '}
              for the night
            </p>
            <p className="text-sm text-ink-400">
              All in. No taxes, no fees, paid today. A room is your RSVP to the celebration.
            </p>
          </div>
        </div>

        {amenities.length > 0 && (
          <ul className="mt-5 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-ink-400">
            {amenities.map((a) => (
              <li key={a.id}>{a.name}</li>
            ))}
            {room.amenities.length > amenities.length && (
              <li>+{room.amenities.length - amenities.length} more</li>
            )}
          </ul>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-4 pt-8">
          {room.soldOut ? (
            <AvailabilityBadge soldOut remaining={0} />
          ) : (
            <Link to={`/checkout/${room.slug}${stayQs}`} className="btn-primary">
              Reserve for {money(room.fromRate)}
            </Link>
          )}
          <Link to={`/rooms/${room.slug}${stayQs}`} className="btn-outline">
            Photos and details
          </Link>
          {!room.soldOut && <AvailabilityBadge soldOut={false} remaining={room.remaining} />}
        </div>
      </div>
    </article>
  );
}
